/**
 * Compare Insights Component
 *
 * Displays key insights from fund comparison analysis.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { Fund } from "@curie/shared";
import { calculateCurieScore } from "./compare-utils";

// ============================================================================
// Types
// ============================================================================

interface CompareInsightsProps {
  funds: Fund[];
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * CompareInsights
 *
 * Renders a card with key insights derived from comparing funds.
 */
export function CompareInsights({ funds }: CompareInsightsProps) {
  if (funds.length < 2) {
    return null;
  }

  // Calculate insights
  const lowestExpense = funds.reduce((min, f) =>
    f.expenseRatio < min.expenseRatio ? f : min
  );

  const bestReturns = funds.reduce((max, f) =>
    f.returns5Y > max.returns5Y ? f : max
  );

  const bestScore = funds.reduce((max, f) =>
    calculateCurieScore(f) > calculateCurieScore(max) ? f : max
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Key Insights</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm">
          <span className="font-semibold text-green-600">
            {lowestExpense.amcName}
          </span>{" "}
          has the lowest expense ratio at {lowestExpense.expenseRatio.toFixed(2)}%,
          making it more cost-effective for long-term investors.
        </p>

        <p className="text-sm">
          <span className="font-semibold text-green-600">
            {bestReturns.amcName}
          </span>{" "}
          has delivered the best 5-year returns at{" "}
          {bestReturns.returns5Y >= 0 ? "+" : ""}
          {bestReturns.returns5Y.toFixed(1)}%.
        </p>

        <p className="text-sm">
          <span className="font-semibold text-green-600">
            {bestScore.amcName}
          </span>{" "}
          has the highest Curie Score of {calculateCurieScore(bestScore)},
          indicating better overall performance consistency.
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * Placeholder for performance chart
 */
export function CompareChartPlaceholder() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Performance Chart</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex h-48 items-center justify-center rounded-lg bg-muted">
          <p className="text-muted-foreground">
            Comparison chart coming soon
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
